import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Badge, Table, Tabs, Tab } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import {
  getFinancialRatios,
  getTrendAnalysis,
  getComparativeAnalysis,
  getCashFlowForecast,
  getWhatIfScenario,
} from '../../../services/admin';
import moment from 'moment';
import { toast } from 'react-toastify';

const FinancialAnalysis = () => {
  const [activeTab, setActiveTab] = useState('ratios');
  const [loading, setLoading] = useState(false);

  // Ratios
  const [ratios, setRatios] = useState(null);
  const [ratiosDate, setRatiosDate] = useState(moment().format('YYYY-MM-DD'));

  // Trends
  const [trends, setTrends] = useState(null);
  const [trendMonths, setTrendMonths] = useState(12);

  // Comparative
  const [comparative, setComparative] = useState(null);
  const [period1From, setPeriod1From] = useState(moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD'));
  const [period1To, setPeriod1To] = useState(moment().subtract(1, 'year').endOf('month').format('YYYY-MM-DD'));
  const [period2From, setPeriod2From] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [period2To, setPeriod2To] = useState(moment().endOf('month').format('YYYY-MM-DD'));

  // Forecast
  const [forecast, setForecast] = useState(null);
  const [forecastMonths, setForecastMonths] = useState(6);

  // What-If
  const [whatIf, setWhatIf] = useState(null);
  const [whatIfPercentage, setWhatIfPercentage] = useState(10);

  useEffect(() => {
    if (activeTab === 'ratios') loadRatios();
    if (activeTab === 'trends') loadTrends();
    if (activeTab === 'forecast') loadForecast();
  }, [activeTab]);

  const loadRatios = () => {
    setLoading(true);
    getFinancialRatios({ as_on_date: ratiosDate })
      .then((res) => {
        setRatios(res.data.ratios);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load financial ratios');
        setLoading(false);
      });
  };

  const loadTrends = () => {
    setLoading(true);
    getTrendAnalysis({ months: trendMonths })
      .then((res) => {
        setTrends(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load trend analysis');
        setLoading(false);
      });
  };

  const loadComparative = () => {
    setLoading(true);
    getComparativeAnalysis({
      period1_from: period1From,
      period1_to: period1To,
      period2_from: period2From,
      period2_to: period2To,
    })
      .then((res) => {
        setComparative(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load comparative analysis');
        setLoading(false);
      });
  };

  const loadForecast = () => {
    setLoading(true);
    getCashFlowForecast({ months: forecastMonths })
      .then((res) => {
        setForecast(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load cash flow forecast');
        setLoading(false);
      });
  };

  const loadWhatIf = () => {
    setLoading(true);
    getWhatIfScenario({ percentage: whatIfPercentage })
      .then((res) => {
        setWhatIf(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load what-if scenario');
        setLoading(false);
      });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getRatioColor = (value, type) => {
    // Color coding based on ratio interpretation
    if (type === 'liquidity' && value >= 1.5) return 'success';
    if (type === 'liquidity' && value >= 1) return 'warning';
    if (type === 'profitability' && value >= 15) return 'success';
    if (type === 'profitability' && value >= 10) return 'warning';
    return 'danger';
  };

  // Chart data for trends
  const trendChartData = trends ? {
    labels: trends?.trends?.map(t => t.period),
    datasets: [
      {
        label: 'Revenue',
        data: trends?.trends?.map(t => t.revenue),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Expense',
        data: trends?.trends?.map(t => t.expense),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
      {
        label: 'Profit',
        data: trends?.trends?.map(t => t.profit),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
      },
    ],
  } : null;

  // Chart data for forecast
  const forecastChartData = forecast ? {
    labels: forecast?.forecast?.map(f => f.period),
    datasets: [
      {
        label: 'Projected Cash In',
        data: forecast?.forecast?.map(f => f.projected_cash_in),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Projected Cash Out',
        data: forecast?.forecast?.map(f => f.projected_cash_out),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
      {
        label: 'Projected Cash Balance',
        data: forecast?.forecast?.map(f => f.projected_cash_balance),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
      },
    ],
  } : null;

  return (
    <div className="main-container container-fluid">
      <div className="page-header">
        <div>
          <h1 className="page-title">Advanced Financial Analysis</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/superadmin/dashboard">Dashboard</a></li>
            <li className="breadcrumb-item active">Financial Analysis</li>
          </ol>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        {/* Financial Ratios Tab */}
        <Tab eventKey="ratios" title="Financial Ratios">
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>As On Date</Form.Label>
                <Form.Control
                  type="date"
                  value={ratiosDate}
                  onChange={(e) => setRatiosDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button onClick={loadRatios} disabled={loading}>
                {loading ? 'Loading...' : 'Calculate Ratios'}
              </Button>
            </Col>
          </Row>

          {ratios && (
            <>
              {/* Liquidity Ratios */}
              <Card className="mb-3">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">Liquidity Ratios</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Current Ratio</h6>
                        <h2 className={`text-${getRatioColor(ratios.liquidity_ratios.current_ratio, 'liquidity')}`}>
                          {ratios.liquidity_ratios.current_ratio}
                        </h2>
                        <small className="text-muted">{ratios.liquidity_ratios.interpretation.current_ratio}</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Quick Ratio</h6>
                        <h2 className={`text-${getRatioColor(ratios.liquidity_ratios.quick_ratio, 'liquidity')}`}>
                          {ratios.liquidity_ratios.quick_ratio}
                        </h2>
                        <small className="text-muted">{ratios.liquidity_ratios.interpretation.quick_ratio}</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Cash Ratio</h6>
                        <h2>{ratios.liquidity_ratios.cash_ratio}</h2>
                        <small className="text-muted">{ratios.liquidity_ratios.interpretation.cash_ratio}</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Profitability Ratios */}
              <Card className="mb-3">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">Profitability Ratios</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Gross Profit Margin</h6>
                        <h2>{ratios.profitability_ratios.gross_profit_margin}%</h2>
                        <small className="text-muted">{ratios.profitability_ratios.interpretation.gross_profit_margin}</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Net Profit Margin</h6>
                        <h2>{ratios.profitability_ratios.net_profit_margin}%</h2>
                        <small className="text-muted">{ratios.profitability_ratios.interpretation.net_profit_margin}</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">ROA</h6>
                        <h2 className={`text-${getRatioColor(ratios.profitability_ratios.return_on_assets, 'profitability')}`}>
                          {ratios.profitability_ratios.return_on_assets}%
                        </h2>
                        <small className="text-muted">{ratios.profitability_ratios.interpretation.roa}</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">ROE</h6>
                        <h2 className={`text-${getRatioColor(ratios.profitability_ratios.return_on_equity, 'profitability')}`}>
                          {ratios.profitability_ratios.return_on_equity}%
                        </h2>
                        <small className="text-muted">{ratios.profitability_ratios.interpretation.roe}</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Efficiency & Solvency Ratios */}
              <Row>
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-info text-white">
                      <h5 className="mb-0">Efficiency Ratios</h5>
                    </Card.Header>
                    <Card.Body>
                      <Table bordered>
                        <tbody>
                          <tr>
                            <td>Asset Turnover</td>
                            <td className="text-end"><strong>{ratios.efficiency_ratios.asset_turnover}</strong></td>
                          </tr>
                          <tr>
                            <td>Fixed Asset Turnover</td>
                            <td className="text-end"><strong>{ratios.efficiency_ratios.fixed_asset_turnover}</strong></td>
                          </tr>
                          <tr>
                            <td>Current Asset Turnover</td>
                            <td className="text-end"><strong>{ratios.efficiency_ratios.current_asset_turnover}</strong></td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-warning">
                      <h5 className="mb-0">Solvency Ratios</h5>
                    </Card.Header>
                    <Card.Body>
                      <Table bordered>
                        <tbody>
                          <tr>
                            <td>Debt to Equity Ratio</td>
                            <td className="text-end"><strong>{ratios.solvency_ratios.debt_to_equity_ratio}</strong></td>
                          </tr>
                          <tr>
                            <td>Debt Ratio</td>
                            <td className="text-end"><strong>{ratios.solvency_ratios.debt_ratio}%</strong></td>
                          </tr>
                          <tr>
                            <td>Equity Ratio</td>
                            <td className="text-end"><strong>{ratios.solvency_ratios.equity_ratio}%</strong></td>
                          </tr>
                          <tr>
                            <td>Interest Coverage</td>
                            <td className="text-end"><strong>{ratios.solvency_ratios.interest_coverage}</strong></td>
                          </tr>
                        </tbody>
                      </Table>
                      <small className="text-muted">{ratios.solvency_ratios.interpretation.debt_to_equity}</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Tab>

        {/* Trend Analysis Tab */}
        <Tab eventKey="trends" title="Trend Analysis">
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Number of Months</Form.Label>
                <Form.Select value={trendMonths} onChange={(e) => setTrendMonths(e.target.value)}>
                  <option value="6">Last 6 Months</option>
                  <option value="12">Last 12 Months</option>
                  <option value="24">Last 24 Months</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button onClick={loadTrends} disabled={loading}>
                {loading ? 'Loading...' : 'Load Trends'}
              </Button>
            </Col>
          </Row>

          {trends && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Card>
                    <Card.Body className="text-center">
                      <h6 className="text-muted">Revenue Growth</h6>
                      <h3 className={trends?.growth_rates?.revenue_growth >= 0 ? 'text-success' : 'text-danger'}>
                        {trends?.growth_rates?.revenue_growth?.toFixed(2)}%
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card>
                    <Card.Body className="text-center">
                      <h6 className="text-muted">Expense Growth</h6>
                      <h3 className={trends?.growth_rates?.expense_growth <= 0 ? 'text-success' : 'text-danger'}>
                        {trends?.growth_rates?.expense_growth?.toFixed(2)}%
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card>
                    <Card.Body className="text-center">
                      <h6 className="text-muted">Profit Growth</h6>
                      <h3 className={trends?.growth_rates?.profit_growth >= 0 ? 'text-success' : 'text-danger'}>
                        {trends?.growth_rates?.profit_growth?.toFixed(2)}%
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">Revenue, Expense & Profit Trend</h5>
                </Card.Header>
                <Card.Body>
                  {trendChartData && <Line data={trendChartData} options={{ responsive: true }} />}
                </Card.Body>
              </Card>
            </>
          )}
        </Tab>

        {/* Comparative Analysis Tab */}
        <Tab eventKey="comparative" title="Comparative Analysis">
          <Row className="mb-3">
            <Col md={6}>
              <Card>
                <Card.Header>Period 1</Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={period1From}
                          onChange={(e) => setPeriod1From(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={period1To}
                          onChange={(e) => setPeriod1To(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header>Period 2</Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={period2From}
                          onChange={(e) => setPeriod2From(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={period2To}
                          onChange={(e) => setPeriod2To(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Button onClick={loadComparative} disabled={loading} className="mb-3">
            {loading ? 'Loading...' : 'Compare Periods'}
          </Button>

          {comparative && (
            <Card>
              <Card.Body>
                <Table bordered hover>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th className="text-end">Period 1</th>
                      <th className="text-end">Period 2</th>
                      <th className="text-end">Variance</th>
                      <th className="text-end">% Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Revenue</strong></td>
                      <td className="text-end">{formatCurrency(comparative.period1.revenue)}</td>
                      <td className="text-end">{formatCurrency(comparative.period2.revenue)}</td>
                      <td className="text-end">{formatCurrency(comparative.variance.revenue)}</td>
                      <td className={`text-end ${comparative.percentage_change.revenue >= 0 ? 'text-success' : 'text-danger'}`}>
                        {comparative.percentage_change.revenue.toFixed(2)}%
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Expense</strong></td>
                      <td className="text-end">{formatCurrency(comparative.period1.expense)}</td>
                      <td className="text-end">{formatCurrency(comparative.period2.expense)}</td>
                      <td className="text-end">{formatCurrency(comparative.variance.expense)}</td>
                      <td className={`text-end ${comparative.percentage_change.expense <= 0 ? 'text-success' : 'text-danger'}`}>
                        {comparative.percentage_change.expense.toFixed(2)}%
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Profit</strong></td>
                      <td className="text-end">{formatCurrency(comparative.period1.profit)}</td>
                      <td className="text-end">{formatCurrency(comparative.period2.profit)}</td>
                      <td className="text-end">{formatCurrency(comparative.variance.profit)}</td>
                      <td className={`text-end ${comparative.percentage_change.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                        {comparative.percentage_change.profit.toFixed(2)}%
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Profit Margin</strong></td>
                      <td className="text-end">{comparative.period1.profit_margin.toFixed(2)}%</td>
                      <td className="text-end">{comparative.period2.profit_margin.toFixed(2)}%</td>
                      <td className="text-end">{comparative.variance.profit_margin.toFixed(2)}%</td>
                      <td className="text-end">-</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Tab>

        {/* Cash Flow Forecast Tab */}
        <Tab eventKey="forecast" title="Cash Flow Forecast">
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Forecast Period (Months)</Form.Label>
                <Form.Select value={forecastMonths} onChange={(e) => setForecastMonths(e.target.value)}>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button onClick={loadForecast} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Forecast'}
              </Button>
            </Col>
          </Row>

          {forecast && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Card className="bg-primary text-white">
                    <Card.Body className="text-center">
                      <h6>Current Cash Position</h6>
                      <h3>{formatCurrency(forecast.current_cash)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={8}>
                  <Card>
                    <Card.Header>Historical Average (Last 12 Months)</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <small className="text-muted">Avg Cash In</small>
                          <h5>{formatCurrency(forecast?.historical_average?.cash_in)}</h5>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted">Avg Cash Out</small>
                          <h5>{formatCurrency(forecast?.historical_average?.cash_out)}</h5>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted">Avg Net Flow</small>
                          <h5>{formatCurrency(forecast?.historical_average?.net_flow)}</h5>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Header>
                  <h5 className="mb-0">Cash Flow Forecast Chart</h5>
                </Card.Header>
                <Card.Body>
                  {forecastChartData && <Line data={forecastChartData} options={{ responsive: true }} />}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">Detailed Forecast</h5>
                </Card.Header>
                <Card.Body>
                  <Table bordered hover>
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th className="text-end">Projected Cash In</th>
                        <th className="text-end">Projected Cash Out</th>
                        <th className="text-end">Net Flow</th>
                        <th className="text-end">Projected Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forecast?.forecast?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.period}</td>
                          <td className="text-end">{formatCurrency(item.projected_cash_in)}</td>
                          <td className="text-end">{formatCurrency(item.projected_cash_out)}</td>
                          <td className={`text-end ${item.projected_net_flow >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCurrency(item.projected_net_flow)}
                          </td>
                          <td className={`text-end ${item.projected_cash_balance >= 0 ? 'text-primary' : 'text-danger'}`}>
                            <strong>{formatCurrency(item.projected_cash_balance)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          )}
        </Tab>

        {/* What-If Scenario Tab */}
        <Tab eventKey="whatif" title="What-If Analysis">
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Percentage Change (%)</Form.Label>
                <Form.Control
                  type="number"
                  value={whatIfPercentage}
                  onChange={(e) => setWhatIfPercentage(e.target.value)}
                  min="1"
                  max="100"
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button onClick={loadWhatIf} disabled={loading}>
                {loading ? 'Loading...' : 'Run Scenario'}
              </Button>
            </Col>
          </Row>

          {whatIf && (
            <>
              <Card className="mb-3">
                <Card.Header className="bg-secondary text-white">
                  <h5 className="mb-0">Current State (Last Month)</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <small className="text-muted">Revenue</small>
                      <h5>{formatCurrency(whatIf.current.revenue)}</h5>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted">Expense</small>
                      <h5>{formatCurrency(whatIf.current.expense)}</h5>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted">Profit</small>
                      <h5>{formatCurrency(whatIf.current.profit)}</h5>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted">Margin</small>
                      <h5>{whatIf.current.margin.toFixed(2)}%</h5>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row>
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Header className="bg-success text-white">
                      <h6 className="mb-0">Scenario 1: Revenue Increase ({whatIfPercentage}%)</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td>Revenue:</td>
                            <td className="text-end"><strong>{formatCurrency(whatIf.scenarios.revenue_increase.revenue)}</strong></td>
                          </tr>
                          <tr>
                            <td>Expense:</td>
                            <td className="text-end">{formatCurrency(whatIf.scenarios.revenue_increase.expense)}</td>
                          </tr>
                          <tr>
                            <td>Profit:</td>
                            <td className="text-end"><strong>{formatCurrency(whatIf.scenarios.revenue_increase.profit)}</strong></td>
                          </tr>
                          <tr>
                            <td>Margin:</td>
                            <td className="text-end">{whatIf.scenarios.revenue_increase.margin.toFixed(2)}%</td>
                          </tr>
                        </tbody>
                      </Table>
                      <hr />
                      <div className="text-success">
                        <strong>Impact:</strong><br />
                        Profit Change: {formatCurrency(whatIf.impact.revenue_increase.profit_change)}<br />
                        Margin Change: {whatIf.impact.revenue_increase.margin_change.toFixed(2)}%
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100">
                    <Card.Header className="bg-warning">
                      <h6 className="mb-0">Scenario 2: Expense Reduction ({whatIfPercentage}%)</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td>Revenue:</td>
                            <td className="text-end">{formatCurrency(whatIf.scenarios.expense_reduction.revenue)}</td>
                          </tr>
                          <tr>
                            <td>Expense:</td>
                            <td className="text-end"><strong>{formatCurrency(whatIf.scenarios.expense_reduction.expense)}</strong></td>
                          </tr>
                          <tr>
                            <td>Profit:</td>
                            <td className="text-end"><strong>{formatCurrency(whatIf.scenarios.expense_reduction.profit)}</strong></td>
                          </tr>
                          <tr>
                            <td>Margin:</td>
                            <td className="text-end">{whatIf.scenarios.expense_reduction.margin.toFixed(2)}%</td>
                          </tr>
                        </tbody>
                      </Table>
                      <hr />
                      <div className="text-success">
                        <strong>Impact:</strong><br />
                        Profit Change: {formatCurrency(whatIf.impact.expense_reduction.profit_change)}<br />
                        Margin Change: {whatIf.impact.expense_reduction.margin_change.toFixed(2)}%
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0">Scenario 3: Combined Effect</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td>Revenue:</td>
                            <td className="text-end"><strong>{formatCurrency(whatIf.scenarios.combined.revenue)}</strong></td>
                          </tr>
                          <tr>
                            <td>Expense:</td>
                            <td className="text-end"><strong>{formatCurrency(whatIf.scenarios.combined.expense)}</strong></td>
                          </tr>
                          <tr>
                            <td>Profit:</td>
                            <td className="text-end"><strong className="text-success">{formatCurrency(whatIf.scenarios.combined.profit)}</strong></td>
                          </tr>
                          <tr>
                            <td>Margin:</td>
                            <td className="text-end">{whatIf.scenarios.combined.margin.toFixed(2)}%</td>
                          </tr>
                        </tbody>
                      </Table>
                      <hr />
                      <div className="text-success">
                        <strong>Impact:</strong><br />
                        Profit Change: {formatCurrency(whatIf.impact.combined.profit_change)}<br />
                        Margin Change: {whatIf.impact.combined.margin_change.toFixed(2)}%
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default FinancialAnalysis;
